import { useState, useEffect, useRef } from 'react';
import { useAudio } from '../../context/AudioContext';
import { Slider } from '../ui/slider';
import { Waveform } from './Waveform';
import { 
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX,
  Shuffle, Repeat, Repeat1, Radio, X, ChevronUp, ChevronDown,
  Music2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatTime = (seconds) => {
  if (isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const AudioPlayer = () => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const progressRef = useRef(null);
  const {
    currentTrack, isPlaying, currentTime, duration, volume,
    isShuffle, repeatMode, togglePlay, seek, setVolume,
    playNext, playPrevious, toggleShuffle, toggleRepeat,
    closePlayer, isRadioMode, skipsRemaining, skipRadio, stopRadio,
  } = useAudio();

  if (!currentTrack) return null;

  const handleSkipNext = () => isRadioMode ? skipRadio() : playNext();
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleProgressClick = (e) => {
    if (!progressRef.current || isRadioMode) return;
    const rect = progressRef.current.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    seek(pct * duration);
  };

  // Get artwork URL
  const getArtwork = () => {
    if (currentTrack.cover_art_r2_url) return currentTrack.cover_art_r2_url;
    if (currentTrack.cover_art) {
      if (currentTrack.cover_art.startsWith('http')) return currentTrack.cover_art;
      return `${BACKEND_URL}/api/tracks/${currentTrack.id}/cover`;
    }
    return null;
  };
  const artwork = getArtwork();

  const accentColor = isRadioMode ? '#10b981' : '#00CCFF';
  const accentGlow = isRadioMode ? '0 0 20px rgba(16,185,129,0.3)' : '0 0 20px rgba(0,204,255,0.3)';

  return (
    <>
      {/* Clickable progress bar at top of player. iter_407 — switched
       * from `preserve-dark` (which no longer keeps anything dark in
       * light mode) to `audio-player-dark`, the only opt-in exemption
       * left in the light-theme repaint rules. */}
      <div 
        data-testid="audio-player"
        className="audio-player-dark fixed bottom-0 left-0 right-0 z-50"
        style={{ height: expanded ? '180px' : '72px', transition: 'height 0.3s ease' }}
      >
        {/* Top progress line */}
        <div 
          ref={progressRef}
          onClick={handleProgressClick}
          className="absolute top-0 left-0 right-0 h-1 bg-white/5 cursor-pointer group z-10"
          data-testid="player-progress-bar"
        >
          <div 
            className="h-full transition-all duration-200"
            style={{ 
              width: `${progress}%`,
              background: `linear-gradient(90deg, ${accentColor}, ${accentColor}dd)`,
              boxShadow: accentGlow
            }}
          />
          <div 
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ 
              left: `${progress}%`, 
              transform: `translateX(-50%) translateY(-50%)`,
              background: accentColor,
              boxShadow: accentGlow
            }}
          />
        </div>

        {/* Main player body */}
        <div 
          className="h-full backdrop-blur-xl border-t border-white/[0.06]"
          style={{ 
            background: 'linear-gradient(180deg, rgba(8,8,12,0.95) 0%, rgba(4,4,8,0.98) 100%)'
          }}
        >
          <div className="max-w-screen-2xl mx-auto h-full px-3 md:px-6 flex flex-col justify-center">
            {/* Compact Row */}
            <div className="flex items-center gap-3 md:gap-5">
              
              {/* Close */}
              <button
                data-testid="close-player-btn"
                onClick={closePlayer}
                className="flex-shrink-0 w-7 h-7 flex items-center justify-center text-white/30 hover:text-white/70 transition-colors rounded-full hover:bg-white/5"
              >
                <X size={15} />
              </button>

              {/* Artwork + Track Info */}
              <div className="flex items-center gap-3 min-w-0 w-auto md:w-1/4 flex-shrink-0">
                <div 
                  className="w-11 h-11 md:w-12 md:h-12 rounded-lg flex-shrink-0 overflow-hidden relative cursor-pointer"
                  onClick={() => isRadioMode && navigate('/radio')}
                  style={{ boxShadow: isPlaying ? accentGlow : 'none' }}
                >
                  {artwork ? (
                    <img src={artwork} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div 
                      className="w-full h-full flex items-center justify-center"
                      style={{ 
                        background: isRadioMode 
                          ? 'linear-gradient(135deg, #064e3b, #065f46)' 
                          : 'linear-gradient(135deg, #0c2d48, #0a3d6b)'
                      }}
                    >
                      {isRadioMode ? (
                        <Radio size={20} style={{ color: accentColor }} />
                      ) : (
                        <Music2 size={20} style={{ color: accentColor }} />
                      )}
                    </div>
                  )}
                  {isPlaying && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: accentColor }} />
                  )}
                </div>
                <div className="min-w-0 flex-1 sm:flex-none">
                  {isRadioMode && (
                    <p className="text-[10px] font-mono uppercase tracking-widest mb-0.5 items-center gap-1 hidden sm:flex" style={{ color: accentColor }}>
                      <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: accentColor }} />
                      LIVE RADIO
                    </p>
                  )}
                  <p data-testid="player-track-title" className="text-white/90 font-medium truncate text-xs sm:text-sm tracking-tight leading-tight">
                    {currentTrack.title}
                  </p>
                  <p data-testid="player-track-artist" className="text-white/40 text-[10px] sm:text-xs truncate mt-0.5">
                    {currentTrack.artist}
                  </p>
                </div>
              </div>

              {/* Center Controls */}
              <div className="flex-1 flex items-center justify-center gap-1 md:gap-3">
                {!isRadioMode && (
                  <button
                    data-testid="shuffle-btn"
                    onClick={toggleShuffle}
                    className={`p-1.5 rounded-full transition-all hidden sm:flex items-center justify-center ${
                      isShuffle ? 'text-yellow-400 bg-yellow-400/10' : 'text-white/25 hover:text-white/60 hover:bg-white/5'
                    }`}
                  >
                    <Shuffle size={14} />
                  </button>
                )}
                
                {!isRadioMode && (
                  <button
                    data-testid="prev-btn"
                    onClick={playPrevious}
                    className="p-1.5 rounded-full text-white/60 hover:text-white hover:bg-white/5 transition-all"
                  >
                    <SkipBack size={18} />
                  </button>
                )}
                
                <button
                  data-testid="play-pause-btn"
                  onClick={togglePlay}
                  className="w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95"
                  style={{ 
                    background: accentColor,
                    boxShadow: isPlaying ? accentGlow : 'none'
                  }}
                >
                  {isPlaying ? (
                    <Pause size={20} className="text-black" />
                  ) : (
                    <Play size={20} className="text-black ml-0.5" />
                  )}
                </button>
                
                <button
                  data-testid="next-btn"
                  onClick={handleSkipNext}
                  disabled={isRadioMode && skipsRemaining <= 0}
                  className={`p-1.5 rounded-full transition-all flex items-center gap-1 ${
                    isRadioMode && skipsRemaining <= 0
                      ? 'text-white/10 cursor-not-allowed' 
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                  title={isRadioMode ? `${skipsRemaining} skips remaining` : 'Next'}
                >
                  <SkipForward size={18} />
                  {isRadioMode && (
                    <span className="text-[10px] font-mono text-white/40 hidden md:inline">{skipsRemaining}</span>
                  )}
                </button>

                {!isRadioMode && (
                  <button
                    data-testid="repeat-btn"
                    onClick={toggleRepeat}
                    className={`p-1.5 rounded-full transition-all hidden sm:flex items-center justify-center ${
                      repeatMode !== 'none' ? 'text-yellow-400 bg-yellow-400/10' : 'text-white/25 hover:text-white/60 hover:bg-white/5'
                    }`}
                  >
                    {repeatMode === 'one' ? <Repeat1 size={14} /> : <Repeat size={14} />}
                  </button>
                )}
              </div>

              {/* Time + Volume */}
              <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
                <span className="text-[10px] font-mono text-white/30 hidden md:inline">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>

                <div className="hidden md:flex items-center gap-2">
                  <button
                    data-testid="volume-btn"
                    onClick={() => setVolume(volume === 0 ? 0.7 : 0)}
                    className="text-white/30 hover:text-white/70 transition-colors"
                  >
                    {volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
                  </button>
                  <Slider
                    data-testid="volume-slider"
                    value={[volume * 100]}
                    max={100}
                    step={1}
                    onValueChange={([val]) => setVolume(val / 100)}
                    className="w-20"
                  />
                </div>

                {/* Expand toggle */}
                <button
                  onClick={() => setExpanded(!expanded)}
                  className="p-1 rounded-full text-white/20 hover:text-white/60 hover:bg-white/5 transition-colors"
                  data-testid="player-expand-btn"
                >
                  {expanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                </button>
              </div>
            </div>

            {/* Expanded Waveform Section */}
            {expanded && (
              <div className="mt-2 px-10 md:px-20 animate-in slide-in-from-bottom-2 duration-300">
                <Waveform
                  url={currentTrack ? (currentTrack.file_url 
                    ? (currentTrack.file_url.startsWith('http') ? currentTrack.file_url : `${BACKEND_URL}${currentTrack.file_url}`)
                    : `${BACKEND_URL}/api/stream/track/${currentTrack.filename}`) : null}
                  isPlaying={isPlaying}
                  currentTime={currentTime}
                  duration={duration}
                  onSeek={seek}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Spacer to prevent content from being hidden behind player */}
      <div style={{ height: expanded ? '180px' : '72px', transition: 'height 0.3s ease' }} />
    </>
  );
};
